import { useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { IDU_COLUMNS, POINT_LABELS } from '../constants';

const thStyle = {
  fontWeight: 600,
  color: '#6b7280',
  fontSize: 12,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '10px 16px',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
  background: '#fff',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const tdStyle = {
  padding: '11px 16px',
  fontSize: 13,
  color: '#374151',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
  cursor: 'default',
};

const clickableCellStyle = {
  cursor: 'pointer',
  color: '#2050ff',
  fontWeight: 500,
  userSelect: 'none',
};

function displayValue(col, rawValue) {
  if (col.format) return col.format(rawValue);
  return POINT_LABELS[col.key]?.[rawValue] ?? '–';
}

function HeaderCheckbox({ idus, selectedIds, onSelectAll }) {
  const ref = useRef(null);
  const allChecked = idus.length > 0 && idus.every(idu => selectedIds.includes(idu.iduDeviceId));
  const someChecked = idus.some(idu => selectedIds.includes(idu.iduDeviceId));
  if (ref.current) ref.current.indeterminate = someChecked && !allChecked;
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={allChecked}
      onChange={e => onSelectAll(e.target.checked)}
      style={{ cursor: 'pointer', accentColor: '#2050ff', width: 14, height: 14 }}
    />
  );
}

function IduTable({ idus, loading, onCellClick, isGatewaySelected, selectedIds, onSelectChange, onSelectAll }) {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner animation="border" style={{ color: '#2050ff' }} />
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Manrope' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 40, textAlign: 'center' }}>
              {idus.length > 0 && (
                <HeaderCheckbox idus={idus} selectedIds={selectedIds} onSelectAll={onSelectAll} />
              )}
            </th>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Device ID</th>
            {IDU_COLUMNS.map(col => (
              <th key={col.key} style={thStyle}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {idus.length === 0 ? (
            <tr>
              <td
                colSpan={IDU_COLUMNS.length + 3}
                style={{ ...tdStyle, textAlign: 'center', color: '#aaa', padding: 32 }}
              >
                {isGatewaySelected ? 'No IDUs found for this gateway' : 'Please select a gateway'}
              </td>
            </tr>
          ) : (
            idus.map(idu => {
              const s = idu.latestStatus || {};
              const isChecked = selectedIds.includes(idu.iduDeviceId);
              return (
                <tr
                  key={idu.iduDeviceId}
                  style={{ transition: 'background 0.1s', background: isChecked ? 'rgba(32,80,255,0.04)' : undefined }}
                  onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = '#fafafa'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isChecked ? 'rgba(32,80,255,0.04)' : '#fff'; }}
                >
                  <td style={{ ...tdStyle, textAlign: 'center', width: 40 }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => onSelectChange(idu.iduDeviceId, e.target.checked)}
                      style={{ cursor: 'pointer', accentColor: '#2050ff', width: 14, height: 14 }}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    {idu.name || ''}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    {idu.iduDeviceId || ''}
                  </td>
                  {IDU_COLUMNS.map(col => {
                    const val = s[col.key];
                    const statusColor = col.key === 'Status'
                      ? (val === 1 ? '#16a34a' : '#dc2626')
                      : undefined;
                    return (
                      <td
                        key={col.key}
                        style={col.controllable
                          ? { ...tdStyle, ...clickableCellStyle, ...(statusColor && { color: statusColor }) }
                          : tdStyle}
                        onClick={col.controllable ? () => onCellClick(idu, col.key, val) : undefined}
                      >
                        {displayValue(col, val)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default IduTable;
